/**
 * Line pricing on the order builder (procurement.md §2, round 11): the Books item sell price when it is set, else
 * cost x (1 + hardware_default_markup_pct/100) with the setting at 75, else a per-line override. The source is always
 * shown next to the price. This is a preview: the API re-derives the price when it builds the order card.
 */
import type { ProductOut, SellSource } from './api'

export const DEFAULT_MARKUP_PCT = 75

/** "12.5" / "12.50" / "12" -> 1250. Anything else -> null (never a silent 0). */
export function toCents(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined) return null
  const m = String(value)
    .trim()
    .match(/^(\d+)(?:\.(\d{1,2}))?$/)
  if (!m) return null
  return Number(m[1]) * 100 + Number((m[2] ?? '').padEnd(2, '0'))
}

export function fromCents(cents: number): string {
  return (cents / 100).toFixed(2)
}

export interface LinePrice {
  source: SellSource
  /** null when the price cannot be shown (cost withheld for this role, or nothing in Books to derive it from). */
  unitSell: string | null
  label: string
}

export function linePrice(product: ProductOut, markupPct: number | null | undefined, override?: string | null): LinePrice {
  const pct = markupPct ?? DEFAULT_MARKUP_PCT
  if (override !== undefined && override !== null && override.trim() !== '') {
    const cents = toCents(override)
    return { source: 'override', unitSell: cents === null ? null : fromCents(cents), label: 'Override' }
  }
  const books = toCents(product.sell_price)
  if (books !== null && books > 0) return { source: 'books_price', unitSell: fromCents(books), label: 'Books price' }
  const cost = toCents(product.cost)
  if (cost === null) {
    return {
      source: 'markup',
      unitSell: null,
      label: product.cost === undefined ? `Cost +${pct}% (cost withheld for your role)` : `Cost +${pct}% (no cost in Books)`,
    }
  }
  return { source: 'markup', unitSell: fromCents(Math.round((cost * (100 + pct)) / 100)), label: `Cost +${pct}%` }
}

export function money(value: string | null | undefined, currency = 'CAD'): string {
  if (value === null || value === undefined) return 'withheld'
  const n = Number(value)
  if (!Number.isFinite(n)) return value
  return new Intl.NumberFormat('en-CA', { style: 'currency', currency }).format(n)
}
